import './EventsList.scss';
import '../../../Admin.scss';
import '../../../../../styles/_table.scss';
import { LoadingPlaceholder, Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import { Paragraph } from 'components/TextComps/Paragraph';
import SectionTitle from 'components/TextComps/SectionTitle';
import { useDeleteAdminTrackmanEvent, useGetAdminTrackmanEventsList } from 'queryHooks/Admin/trackmanEvents';
import { useState } from 'react';
import { AdminPagesRelativeRoutes } from 'Routes';
import { AdminEventModel } from 'types/admin/trackmanEventsAdmin';
import { AdminEventSortKeys, eventDataSorter } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as WarningIcon } from 'images/icons/Warning.svg';
import { Link } from 'react-router-dom';
import { DeleteListItemModal } from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import dayjs from 'dayjs';

type TableRowData = {
  id: string;
  title: string;
  membersOnly: string;
  state: string;
  visible: string;
  isUpcomingEventPassed: boolean;
  isRecordingMissing: boolean;
  createdAt: string;
  updatedAt: string;
};

export const EventsListNew = () => {
  useSetPageTitle({ title: 'Events List' });

  const [filter, setFilter] = useState<string>('');
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<AdminEventSortKeys>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);

  const { data: eventsList, isPending } = useGetAdminTrackmanEventsList();
  const filteredList: AdminEventModel[] = eventsList
    ? eventDataSorter(eventsList.events, sortByProperty, sortOrder)?.filter((event) =>
        event.title.toLowerCase().includes(filter.toLowerCase())
      )
    : [];

  const rowsDisplay: TableRowData[] = filteredList.map((eventItem) => ({
    id: eventItem.id,
    title: eventItem.title,
    membersOnly: eventItem.premiumEvent ? 'Yes' : 'No',
    state: eventItem.recordingReady
      ? 'On demand'
      : !eventItem.recordingReady && new Date(eventItem.startDate) < new Date()
        ? 'Event has passed'
        : 'Upcoming',
    visible: eventItem.visible ? 'Yes' : 'No',
    isUpcomingEventPassed: !eventItem.recordingReady && new Date(eventItem.startDate) < new Date(),
    isRecordingMissing: !eventItem.recordingReady && eventItem.locationType === 'Online' && new Date(eventItem.startDate) < new Date(),
    createdAt: dayjs(eventItem.createdAt).format('MMM DD, YYYY'),
    updatedAt: dayjs(eventItem.updatedAt).format('MMM DD, YYYY'),
  }));

  const deleteEvent = useDeleteAdminTrackmanEvent();

  const handleDeleteClick = (id: string) => {
    setEventToDelete(id);
    setIsModalOn(true);
  };

  const handleDeleteConfirm = () => {
    if (eventToDelete && !deleteEvent.isPending) {
      deleteEvent.mutate(eventToDelete, {
        onSettled: () => {
          setEventToDelete(null);
          setIsModalOn(false);
        },
      });
    }
  };

  const handleSortChange = (property: AdminEventSortKeys, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property as AdminEventSortKeys);
    setSortOrder(order);
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Events List</h1>
      </div>
      <div className='admin-content-container'>
        {!isPending ? (
          filteredList?.length > 0 ? (
            <>
              <div className='admin-list-header'>
                <HeaderSearchbar searchValue={filter} onChange={(filter) => setFilter(filter)} placeholder='Search Events' />
                <HeaderCreateButton buttonTitle='New event' route={AdminPagesRelativeRoutes.EventEditor} />
              </div>

              <div className='tmu-table-wrapper events-table-wrapper'>
                <Table className='tmu-table admin-table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Name
                        <SortOrderButton
                          isActive={sortByProperty === 'title'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                        />
                      </TableCell>
                      <TableCell>
                        Members only
                        <SortOrderButton
                          isActive={sortByProperty === 'premiumEvent'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('premiumEvent', sortOrder)}
                        />
                      </TableCell>
                      <TableCell>
                        State
                        <SortOrderButton
                          isActive={sortByProperty === 'state'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('state', sortOrder)}
                        />
                      </TableCell>
                      <TableCell>
                        Visible
                        <SortOrderButton
                          isActive={sortByProperty === 'visible'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                        />
                      </TableCell>
                      <TableCell>
                        Date created
                        <SortOrderButton
                          isActive={sortByProperty === 'createdAt'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                        />
                      </TableCell>
                      <TableCell>
                        Last edited
                        <SortOrderButton
                          isActive={sortByProperty === 'updatedAt'}
                          sortOrder={sortOrder}
                          onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                        />
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rowsDisplay.length ? (
                      rowsDisplay.map((rowData) => {
                        return (
                          <EventsTableRow
                            key={rowData.id}
                            rowData={rowData}
                            onDeleteClick={handleDeleteClick}
                            isDeletePending={deleteEvent.isPending}
                          />
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Paragraph>
                            <>No events found</>
                          </Paragraph>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className='empty-state-card'>
              <div>
                <SectionTitle>
                  <>No Events added yet.</>
                </SectionTitle>
                <Paragraph>
                  <>No Events have been added yet.</>
                </Paragraph>
                <Paragraph>
                  <>Create one to get started</>
                </Paragraph>
              </div>
              <HeaderCreateButton
                buttonTitle='Create Event'
                route={AdminPagesRelativeRoutes.EventEditor}
                customClasses='secondary create-event-button'
              />
            </div>
          )
        ) : (
          <LoadingPlaceholder />
        )}
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={handleDeleteConfirm} itemName='event' />
    </>
  );
};

const EventsTableRow = ({
  rowData,
  onDeleteClick,
  isDeletePending,
}: {
  rowData: TableRowData;
  onDeleteClick: (id: string) => void;
  isDeletePending: boolean;
}) => {
  return (
    <TableRow hover tabIndex={-1} key={rowData.id} className={`${rowData.isRecordingMissing ? 'passed-event' : ''}`}>
      <TableCell>{rowData.title}</TableCell>
      <TableCell>{rowData.membersOnly}</TableCell>
      <TableCell>
        {rowData.isRecordingMissing ? (
          <div className='expiry-warning'>
            <WarningIcon />
            <span>Missing recording</span>
          </div>
        ) : (
          <div className={`${rowData.state === 'Upcoming' ? 'upcoming' : 'on-demand'}`}>{rowData.state}</div>
        )}
      </TableCell>
      <TableCell>{rowData.visible}</TableCell>
      <TableCell>{rowData.createdAt}</TableCell>
      <TableCell>{rowData.updatedAt}</TableCell>
      <TableCell>
        <div className='action-buttons'>
          <Link to={`/Admin/${AdminPagesRelativeRoutes.EventEditor}/${rowData.id}`} className='row-button'>
            <PencilIcon />
          </Link>
          <button
            className={`row-button${isDeletePending ? ' disabled' : ''}`}
            onClick={() => onDeleteClick(rowData.id)}
            disabled={isDeletePending}
          >
            <TrashIcon />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};
